import React, { useState, useEffect } from "react"
import { useRouter } from "next/router"
import { useSelector } from "react-redux"
import cx from "classnames"
import _get from "lodash/get"

import { selectCartState } from "@/store/features/cartSlice"
import { selectGenericState } from "@/store/features/genericSlice"
import { selectPdpState } from "@/store/features/pdpSlice"

import { NumericFormat } from "react-number-format"
import Button from "@/components/core/Button/Button"
import Image from "@/components/Shared/Image"

import { replaceAddToCartDatalayerPush } from "@/components/ProductDetail/v3/AddToCart/analytics"
import { getAnalyticsData } from "@/components/ProductDetail/v3/StickyHeader/analytics"

import styles from "@/components/ProductDetail/v3/StickyHeader/index.module.scss"

const StickyHeader = props => {
  const {
    staticTexts: {
      addToCart = "",
      notifyMailCTA = "",
      findSimilar = "",
      preOrder = "",
      specialOrder = "",
      availColorCTA = "",
    },
    productStock = "",
    backOrder = "",
    disableEcommerce = "",
    replaceAddToCart = () => {},
    replaceAddToCartCtaLabel = "",
    price = 0,
    onAddToCart = () => {},
    sku = "",
    isComboSku = "",
    customerBrandName = "",
    imgUrl = "",
    title = "",
    color = "",
    currencySign = "",
    isDiscontinued = "",
    isColorDiscontinued = "",
    isRegionSoldOnline = "",
    onAvailableColor = () => {},
    handleScroll = () => {},
    replaceAddToCartCtaLink = "",
    linkNewTab = "",
    description = "",
    analyticData = {},
    productPriceSpiderIncluded = "",
    isDynamicBundle = false,
    disableBundleAddToCart = false,
  } = props || {}

  const router = useRouter()
  const { label, url } = color
  const [showBuyBoxArea, setShowBuyBoxArea] = useState(true)
  const itemStatus = _get(props, "itemStatus", "available")
  const { cart } = useSelector(selectCartState)
  const [loading, setLoading] = useState(false)
  const { featureFlags } = useSelector(selectGenericState)
  const {
    pdp: { data: { primaryBundleSKU_s: primaryBundleSKUs = "" } = {} } = {},
  } = useSelector(selectPdpState)

  const isBundleProduct = Boolean(primaryBundleSKUs)

  const { MYSQLCONNSTR_DISABLE_ADD_CART: disableAddcart } = featureFlags

  let buttonText = addToCart

  const analyticsAddToCart = getAnalyticsData({
    itemStatus,
    productStock,
    backOrder,
    specialOrder: props.specialOrder,
  })

  if (itemStatus === "outOfStock" || !(productStock > 0)) {
    buttonText = notifyMailCTA
    if (
      (!isBundleProduct && (specialOrder === "Yes" || backOrder === "Yes")) ||
      isDynamicBundle
    ) {
      buttonText = addToCart
    }
  } else if (itemStatus === "discontinued") {
    buttonText = findSimilar
  } else if (itemStatus === "preOrder") {
    buttonText = preOrder
  } else if (itemStatus === "specialOrder") {
    buttonText = specialOrder
  }

  const analyticsWhereToBuy =
    disableEcommerce &&
    replaceAddToCart &&
    replaceAddToCartCtaLabel &&
    JSON.stringify({
      clickInternalLinks: "true",
      eventAction: `pdp:sticky nav:${replaceAddToCartCtaLabel}`,
      eventMsg: "n/a",
      eventName: `pdp:sticky nav:${replaceAddToCartCtaLabel}`,
      eventStatus: "n/a",
      eventType: "navigation",
      internalLinkName: `${replaceAddToCartCtaLabel}`,
      internalLinkPosition: "header",
      internalLinkType: `pdp:${replaceAddToCartCtaLabel}`,
      internalLinkURL: "n/a",
      internalLinkZoneName: `pdp:${replaceAddToCartCtaLabel}`,
    })

  useEffect(() => {
    if (productPriceSpiderIncluded) {
      setShowBuyBoxArea(false)
    } else if (!price) {
      setShowBuyBoxArea(false)
    } else {
      setShowBuyBoxArea(true)
    }
  }, [productPriceSpiderIncluded, price])

  const onAddToCartCb = () => {
    setLoading(false)
  }

  const handleClick = () => {
    setLoading(true)
    if (buttonText === notifyMailCTA) {
      window.scroll({
        top: 800,
        left: 0,
        behavior: "smooth",
      })
    } else if (isDynamicBundle) {
      onAddToCart(onAddToCartCb)
    } else {
      if (analyticsAddToCart && window.adobeDataLayer) {
        // handle Requirement#19 Sticky Nav Add To Cart
        const eventInfo = JSON.parse(analyticsAddToCart)
        const { adobeDataLayer: { getState } = {} } = window
        const page = (getState && getState("page")) || {}
        if (!page.cart) {
          page.cart = { cartID: cart.id }
        } else {
          page.cart.cartID = cart.id
        }
        const otherInfo = {}
        if (page?.productInfo) {
          otherInfo.productInfo = page.productInfo
          delete page.productInfo
        }
        window.adobeDataLayer.push({
          event: "cmp:click",
          eventInfo,
          page,
          ...otherInfo,
        })
      }

      onAddToCart(
        [
          {
            sku: sku,
            quantity: 1,
            isBundle: isComboSku,
            backorder: backOrder === "Yes" ? true : false,
            brand: customerBrandName?.toLowerCase(),
          },
        ],
        onAddToCartCb
      )
    }
  }

  return (
    <div className={styles.pdpStickyHeadWrapper}>
      <div className="pdp-sticky-header container-fluid">
        <div className="pdp-sticky-header__details-wrap container-fluid">
          <div className="pdp-sticky-header__container kf-react-plp-container">
            <Image src={imgUrl} alt={title} width={80} height={80} />
            <div className="pdp-sticky-header__details">
              <span className="pdp-sticky-header__brand-name">{title}</span>
              <p className="pdp-sticky-header__description">{description}</p>
            </div>
            {itemStatus === "available" &&
              color &&
              label !== "Not Applicable" &&
              url && (
                <div className="pdp-sticky-header__color-block">
                  <div
                    className="pdp-sticky-header__color"
                    style={{ backgroundImage: `url('${url}')` }}
                  ></div>
                  <span className="pdp-sticky-header__color-label">
                    {label}
                  </span>
                </div>
              )}
            {showBuyBoxArea && (
              <div
                className={cx({
                  "pdp-sticky-header__right": true,
                  "pdp-sticky-header__right--retailers":
                    productPriceSpiderIncluded,
                })}
              >
                {itemStatus !== "discontinued" && (
                  <div className="pdp-sticky-header__price-block">
                    <span className="pdp-sticky-header__price">
                      <NumericFormat
                        value={price}
                        displayType={"text"}
                        thousandSeparator={true}
                        prefix={currencySign}
                        decimalScale={2}
                        fixedDecimalScale={true}
                      />
                    </span>
                  </div>
                )}
                {isDiscontinued ? (
                  isColorDiscontinued && !disableEcommerce ? (
                    <Button
                      role="button"
                      useDefaultClass={true}
                      label={availColorCTA}
                      size="small"
                      type="primary"
                      onClick={onAvailableColor}
                    />
                  ) : null
                ) : (isRegionSoldOnline &&
                    (productStock > 0 ||
                      specialOrder === "Yes" ||
                      backOrder === "Yes" ||
                      isDynamicBundle) &&
                    !disableEcommerce) ||
                  isBundleProduct ? (
                  <Button
                    useDefaultClass={true}
                    label={buttonText}
                    size="small"
                    type="primary"
                    role={"button"}
                    onClick={handleClick}
                    disabled={disableAddcart || disableBundleAddToCart}
                    loading={loading}
                  />
                ) : isRegionSoldOnline && !disableEcommerce ? (
                  <Button
                    role="button"
                    useDefaultClass={true}
                    className={`${analyticsAddToCart ? "gbh-data-layer" : ""}`}
                    data-gbh-data-layer={`${analyticsAddToCart || null}`}
                    data-gbh-data-layer-custom={analyticData || null}
                    label={buttonText}
                    size="small"
                    type="primary"
                    onClick={handleScroll}
                  />
                ) : null}

                {replaceAddToCart ? (
                  <Button
                    onClick={() => {
                      replaceAddToCartDatalayerPush(
                        replaceAddToCartCtaLabel,
                        replaceAddToCartCtaLink,
                        analyticData
                      )
                      router.push(replaceAddToCartCtaLink)
                    }}
                    useDefaultClass={true}
                    className="gbh-data-layer"
                    data-gbh-data-layer={analyticsWhereToBuy}
                    data-gbh-data-layer-custom={analyticData}
                    role="button"
                    ariaLabel={replaceAddToCartCtaLabel}
                    label={replaceAddToCartCtaLabel}
                    size="small"
                    type="primary"
                    to={replaceAddToCartCtaLink}
                    target={linkNewTab ? "_blank" : "_self"}
                  />
                ) : (
                  ""
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

StickyHeader.defaultProps = {
  productStock: "",
  backOrder: "",
  disableEcommerce: "",
  replaceAddToCart: "",
  replaceAddToCartCtaLabel: "",
  onAddToCart: "",
  sku: "",
  isComboSku: "",
  customerBrandName: "",
  title: "",
  isDiscontinued: "",
  isColorDiscontinued: "",
  isRegionSoldOnline: "",
  onAvailableColor: "",
  handleScroll: "",
  replaceAddToCartCtaLink: "",
  linkNewTab: "",
  imgUrl: "",
  productSku: "",
  brandName: "",
  description: "",
  currencySign: "",
  price: 0,
  itemStatus: "available",
  color: {
    label: "",
    url: "",
  },
  staticTexts: {
    addToCart: "",
    notifyMailCTA: "",
    findSimilar: "",
    preOrder: "",
    specialOrder: "",
    availColorCTA: "",
  },
  isDynamicBundle: false,
  disableBundleAddToCart: false,
}

export default StickyHeader
